import React, {
	useCallback,
	useLayoutEffect,
	useRef,
	useEffect,
	type MouseEvent,
	type SyntheticEvent,
} from 'react';
import { styled } from '@compiled/react';
import { ChecklistButton } from '@atlassiansox/checklist';
import { useAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';

import { token } from '@atlaskit/tokens';

import { useActiveRightSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { PANEL_ID } from '@atlassian/jira-onboarding-checklist-next/src/common/constants.tsx';
import {
	isMetricRunning,
	qsButtonAutoLoadTime,
	qsButtonDismissInteraction,
	qsButtonOpenInteraction,
	qsChecklistMinimiseInteraction,
} from '@atlassian/jira-onboarding-checklist-next/src/common/utils/performance-metrics.tsx';
import { NAVIGATION_APPS_PROFILE_ITEM_NUDGE_ID } from '@atlassian/jira-onboarding-core/src/constants.tsx';
import {
	QUICKSTART_UI_STATUS,
	SAMPLE_DATA_PROJECT_KEYS_TO_HIDE_ON,
} from '@atlassian/jira-onboarding-quickstart-core/src/common/constants.tsx';
import { getProductType } from '@atlassian/jira-onboarding-quickstart-core/src/common/utils/analytics/index.tsx';
import {
	useQuickstartUiStatus,
	useJswOnboarding,
	useQuickstartActions,
} from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fireAnalyticsOnQuickstartDismiss } from '@atlassian/jira-onboarding-quickstart-core/src/utils/analytics/index.tsx';
import {
	AnalyticsEventToProps,
	DRAWER,
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { BUTTON_ACTION_SUBJECT_ID } from '../../../common/constants.tsx';
import { useOpenChecklist } from '../../../controllers/open-checklist/index.tsx';

const ChecklistButtonAnaylticsWrapper = AnalyticsEventToProps('button', {
	onClick: 'clicked',
})(ChecklistButton);

export const JswOnboardingChecklistButton = () => {
	const currentRightSidebarState = useActiveRightSidebarState();
	const [
		,
		{
			nextSetChecklistIsDismissed,
			setNudgeStep,
			setHasNudgeActive,
			setQuickstartButtonRef,
			setIsOpenedViaButton,
		},
	] = useQuickstartActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const openChecklist = useOpenChecklist();
	const [quickstartStatus] = useQuickstartUiStatus();
	const [remoteStateFromHook] = useJswOnboarding();
	const buttonWrapperRef = useRef<HTMLDivElement>(null);

	const currentRightSidebarPanelId = currentRightSidebarState?.panelId;

	// Store the button ref for focus management when panel is minimized
	useEffect(() => {
		if (!fg('jira_a11y_focus_quickstart_button')) {
			return;
		}

		const wrapperElement = buttonWrapperRef.current;
		if (wrapperElement) {
			// Find the actual button element within the wrapper
			const buttonElement = wrapperElement.querySelector('button');
			if (buttonElement) {
				setQuickstartButtonRef(buttonElement);
			}
		}
		return () => {
			setQuickstartButtonRef(null);
		};
	}, [setQuickstartButtonRef]);

	const onDismiss = useCallback(
		(e: MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();
			if (!fg('jira-onboarding-disable-bm3-ufo-instrument')) {
				qsButtonDismissInteraction.start();
			}

			nextSetChecklistIsDismissed(true);

			setNudgeStep(NAVIGATION_APPS_PROFILE_ITEM_NUDGE_ID);
			setHasNudgeActive(true);

			const analyticsEvent = createAnalyticsEvent({});
			fireAnalyticsOnQuickstartDismiss(
				analyticsEvent,
				{
					projectId: remoteStateFromHook.projectState?.projectId,
					productType: getProductType(remoteStateFromHook.projectState?.projectType),
				},
				'button',
			);
		},
		[
			createAnalyticsEvent,
			nextSetChecklistIsDismissed,
			setNudgeStep,
			setHasNudgeActive,
			remoteStateFromHook,
		],
	);

	useLayoutEffect(() => {
		if (isMetricRunning(qsButtonAutoLoadTime)) {
			// QS Button loading completed on page load action
			qsButtonAutoLoadTime.stop();
		}

		if (
			!fg('jira-onboarding-disable-bm3-ufo-instrument') &&
			isMetricRunning(qsChecklistMinimiseInteraction)
		) {
			// User interaction completed for QS Checklist minimise action
			qsChecklistMinimiseInteraction.stop();
		}
	}, []);

	const projectKey = remoteStateFromHook.projectState?.projectKey;

	if (
		projectKey &&
		SAMPLE_DATA_PROJECT_KEYS_TO_HIDE_ON.includes(projectKey) &&
		fg('jfind-mature-example-project-gate')
	) {
		return;
	}

	if (currentRightSidebarPanelId !== PANEL_ID) {
		return null;
	}

	return (
		<TriggerWrapper
			ref={buttonWrapperRef}
			reposition={quickstartStatus === QUICKSTART_UI_STATUS.OPEN}
		>
			<ContextualAnalyticsData sourceType={DRAWER} sourceName={BUTTON_ACTION_SUBJECT_ID}>
				<FireScreenAnalytics
					attributes={{
						projectId: remoteStateFromHook.projectState?.projectId,
						productType: getProductType(remoteStateFromHook.projectState?.projectType),
					}}
				/>
				<ChecklistButtonAnaylticsWrapper
					isOpen={quickstartStatus === QUICKSTART_UI_STATUS.MINIMIZED}
					title="Quickstart"
					onClick={(event: SyntheticEvent<HTMLButtonElement>, analyticsEvent: UIAnalyticsEvent) => {
						if (!fg('jira-onboarding-disable-bm3-ufo-instrument')) {
							qsButtonOpenInteraction.start();
						}

						// Mark that the panel was opened via button click for focus management
						if (fg('jira_a11y_focus_quickstart_button')) {
							setIsOpenedViaButton(true);
						}

						openChecklist({
							trigger: 'JswOnboardingChecklistButton',
						});
						fireUIAnalytics(analyticsEvent, BUTTON_ACTION_SUBJECT_ID, {
							projectId: remoteStateFromHook.projectState?.projectId,
							productType: getProductType(remoteStateFromHook.projectState?.projectType),
						});
					}}
					onDismiss={onDismiss}
				/>
			</ContextualAnalyticsData>
		</TriggerWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TriggerWrapper = styled.div<{ reposition: boolean }>({
	color: token('color.text.subtle'),
	transition: 'margin 220ms',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginBottom: (props) => `${props.reposition ? 56 : 0}px`,
	cursor: 'pointer',
});
